import { Button } from '@sea-snag/ui';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useRef, useState } from 'react';

import { StepInviteMembers } from './step-invite-members';
import { StepMainInfo } from './step-main-info';
import { StepOrgInfo } from './step-org-info';
import { StepThemePicker } from './step-theme-picker';

interface OnboardingWizardProps {
  email: string;
  token: string;
  mode: 'new' | 'new-org';
}

export function OnboardingWizard({ email, token, mode }: OnboardingWizardProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [createdOrgPid, setCreatedOrgPid] = useState<string | null>(null);
  const stepKey = useRef(0);

  const goForward = () => {
    stepKey.current += 1;
    setDirection('forward');
    setCurrentStep((prev) => prev + 1);
  };

  const goBack = () => {
    stepKey.current += 1;
    setDirection('back');
    setCurrentStep((prev) => prev - 1);
  };

  const handleAccountComplete = (orgPid: string) => {
    setCreatedOrgPid(orgPid);
    goForward();
  };

  const handleComplete = () => {
    void navigate({ to: '/$orgPid/dashboard', params: { orgPid: createdOrgPid ?? '' } });
  };

  const animationClass =
    direction === 'forward' ? 'animate-slide-in-right' : 'animate-slide-in-left';

  return (
    <div className="mx-auto w-full max-w-lg space-y-6">
      {currentStep > 0 && (
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={goBack}>
          <ArrowLeft className="size-4" />
          Back
        </Button>
      )}

      <div key={stepKey.current} className={animationClass}>
        {currentStep === 0 &&
          (mode === 'new' ? (
            <StepMainInfo email={email} token={token} onComplete={handleAccountComplete} />
          ) : (
            <StepOrgInfo email={email} token={token} onComplete={handleAccountComplete} />
          ))}

        {currentStep === 1 && createdOrgPid && (
          <StepInviteMembers orgPid={createdOrgPid} onComplete={goForward} />
        )}

        {currentStep === 2 && <StepThemePicker onComplete={handleComplete} />}
      </div>
    </div>
  );
}
